import { CloseOutlined } from '@ant-design/icons';
import { type ChangeEvent, type CSSProperties, type KeyboardEvent, useState } from 'react';
import styled, { css } from 'styled-components';

import { Flex } from '@/components/base';
import Input from '@/components/Inputs/Input';
import Typo from '@/components/Typography/Typo';
import { useToast } from '@/stores/pure/ToastStore';
import keymap from '@/utils/keymap';

type Props = {
  list: string[];
  onErase: (item: string, list: string[]) => void;
  onAdd: (item: string, list: string[]) => void;
  style?: CSSProperties;
  onError?: (message: string) => void;
  disabled?: boolean;
};

const RemarkInput = (props: Props) => {
  const addToast = useToast();
  const [value, setValue] = useState<string>('');

  const eraseKeyword = (index: number) => {
    const newList = [...props.list];
    const target = newList[index];
    if (!target) return;
    newList.splice(index, 1);
    props.onErase(target, newList);
  };

  const addKeyword = () => {
    const hasCheck = props.list.find((str) => str === value);
    if (hasCheck) return addToast('Duplicate remark already exists', 'fail');
    const newList = [...props.list];
    if (value.trim() === '')
      return addToast('Remark value must be not empty', 'fail');
    newList.push(value.trim());
    props.onAdd(value, newList);
    setValue('');
  };

  const onKeywordInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    const commaIndex = newValue.indexOf(',');
    if (commaIndex !== -1) {
      addKeyword();
      return;
    }
    setValue(newValue);
  };

  const onKeywordInputKeydown = (e: KeyboardEvent) => {
    if (keymap.isBackspace(e) && value === '') {
      eraseKeyword(props.list.length - 1);
    }
    if (keymap.isEnter(e)) {
      e.preventDefault();
      addKeyword();
    }
    if (keymap.isEscape(e)) {
      initialize();
    }
  };

  const initialize = () => {
    setValue('');
  };

  return (
    <KeywordsWrapper
      g="4px"
      a="center"
      style={{ ...props.style }}
      disabled={props.disabled}
    >
      {props.list.map((keyword, index) => (
        <KeywordItemStyle key={keyword} a="center">
          <Typo
            type="label3"
            style={{
              maxWidth: 200,
            }}
            ellipsis
          >
            {keyword}
          </Typo>
          <CloseOutlined
            className="btn-erase-keyword"
            onClick={() => eraseKeyword(index)}
          />
        </KeywordItemStyle>
      ))}
      <Input
        value={value}
        onKeyDown={onKeywordInputKeydown}
        onChange={onKeywordInputChange}
        onBlur={initialize}
        disabled={props.disabled}
        hideBorder
        placeholder={
          props.list.length > 0
            ? ''
            : props.disabled
              ? 'Feedback Remark'
              : 'Type list of remark. Split the words by comma(,) or Enter'
        }
        style={{ padding: 0, height: 18, fontSize: 12, flex: 1 }}
      />
    </KeywordsWrapper>
  );
};

const KeywordsWrapper = styled(Flex)<{ disabled?: boolean }>`
  height: auto;
  min-height: 32px;
  border: 1px solid transparent;
  padding: 2px 12px;
  flex-wrap: wrap;
  border-radius: 6px;
  transition: all 0.2s;
  &:focus-within {
    border: 1px solid ${(props) => props.theme.colors.gray300};
    background-color: ${(props) => props.theme.colors.ND02};
  }
  ${({ disabled, theme }) =>
    disabled &&
    css`
      background-color: ${theme.colors.gray300};
      border: none;
      color: ${theme.colors.gray500};
    `}
`;

const KeywordItemStyle = styled(Flex)`
  background-color: ${(props) => props.theme.colors.gray300};
  color: ${(props) => props.theme.colors.gray700};
  height: 24px;
  padding: 0 8px;
  gap: 8px;
  border-radius: 12px;
  .btn-erase-keyword {
    font-size: 12px;
    cursor: pointer;
  }
`;

export default RemarkInput;
